//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { DraftPostMaterialized } from '@bvengineering/universal-scheduling-gateway-service-types';
import { PojoMap } from 'pojo-maps';

import {
  IFacebookPageFeedPostConfig,
  IInstagramFeedAutomaticPostConfig,
  IInstagramFeedManualPostConfig,
  IInstagramReelsAutomaticPostConfig,
  IInstagramReelsManualPostConfig,
  IInstagramStoryGroupAutomaticPostConfig,
  IInstagramStoryGroupPostConfig,
  IOrganicPinConfig,
  ITikTokVideoAutomaticPostConfig,
  ITwitterMediaPostConfig,
  IYouTubeAutomaticPostConfig,
  TScheduledPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { TPostConnection } from '@apis/universal-posts/connections/universalPostConnectionModel';
import {
  UniversalPostType,
  UniversalPostingGroupName,
} from '@apis/universal-posts/universalPostsApiModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { OmitStrict } from '@utils/types/OmitStrict';

export enum DraftQuerySortBy {
  CreatedAt = 'CreatedAt',
  ScheduledTime = 'ScheduledTime',
}

export interface ISearchDraftPostsRequest {
  groups: UniversalPostingGroupName[];
  offset?: number;
  limit?: number;
  after?: Date;
  before?: Date;
  sortBy?: DraftQuerySortBy;
  sortAscending?: boolean;
}

export interface ISearchDraftPostsResponse {
  data: IDraftPost[];
  totalCount: number;
}

export interface IGetDraftPostsResponse {
  data: IDraftPost[];
}

export interface IDraftPostResponse {
  data: IDraftPost;
}

export interface IDraftPost
  extends OmitStrict<
    DraftPostMaterialized,
    | 'config'
    | 'createdAt'
    | 'id'
    | 'mediaItems'
    | 'postingEntityId'
    | 'scheduledTime'
    | 'connections'
  > {
  postType: UniversalPostType;
  config: TScheduledPostConfig;
  createdAt: Date; // Backend only returns as a number so Date is more useful
  id: DraftId;
  mediaItems: PojoMap<Cuid, IMlsMediaItem>;
  postingEntityId: string;
  scheduledTime?: Date; // Backend only returns as a number so Date is more useful
  connections?: TPostConnection[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ICreateDraftPost
  extends OmitStrict<
    IDraftPost,
    'id' | 'createdAt' | 'mediaItems' | 'postType' | 'connections'
  > {}

export interface IUpsertDraftPost extends ICreateDraftPost {
  id?: DraftId;
}

export type TUniversalDraftPost =
  | IDraftFacebookPageFeedPost
  | IDraftInstagramPost
  | IDraftOrganicPin
  | IDraftTikTokVideoAutomaticPost
  | IDraftTweet
  | IDraftYouTubeAutomaticPost;

export type IDraftInstagramPost =
  | IDraftInstagramStoryGroupPost
  | IDraftInstagramStoryGroupAutomaticPost
  | IDraftInstagramReelsManualPost
  | IDraftInstagramFeedManualPost
  | IDraftInstagramReelsAutomaticPost
  | IDraftInstagramFeedAutomaticPost;

// This set of subtypes extend IDraftPost so that we can use them anywhere we would use an IScheduledPost
export interface IDraftFacebookPageFeedPost extends IDraftPost {
  config: IFacebookPageFeedPostConfig;
  postType: UniversalPostType.FacebookPageFeedPost;
}

export interface IDraftOrganicPin extends IDraftPost {
  config: IOrganicPinConfig;
  postType: UniversalPostType.PinterestOrganicPin;
}

export interface IDraftTikTokVideoAutomaticPost extends IDraftPost {
  config: ITikTokVideoAutomaticPostConfig;
  postType: UniversalPostType.TikTokVideoAutomaticPost;
}

export interface IDraftInstagramStoryGroupPost extends IDraftPost {
  config: IInstagramStoryGroupPostConfig;
  postType: UniversalPostType.InstagramStoryGroupPost;
}

export interface IDraftInstagramStoryGroupAutomaticPost extends IDraftPost {
  config: IInstagramStoryGroupAutomaticPostConfig;
  postType: UniversalPostType.InstagramStoryGroupAutomaticPost;
}

export interface IDraftInstagramReelsManualPost extends IDraftPost {
  config: IInstagramReelsManualPostConfig;
  postType: UniversalPostType.InstagramReelsManualPost;
}

export interface IDraftInstagramFeedManualPost extends IDraftPost {
  config: IInstagramFeedManualPostConfig;
  postType: UniversalPostType.InstagramFeedManualPost;
}
export interface IDraftInstagramReelsAutomaticPost extends IDraftPost {
  config: IInstagramReelsAutomaticPostConfig;
  postType: UniversalPostType.InstagramReelsAutomaticPost;
}

export interface IDraftInstagramFeedAutomaticPost extends IDraftPost {
  config: IInstagramFeedAutomaticPostConfig;
  postType: UniversalPostType.InstagramFeedAutomaticPost;
}

export interface IDraftTweet extends IDraftPost {
  config: ITwitterMediaPostConfig;
  postType: UniversalPostType.TwitterMediaPost;
}

export interface IDraftYouTubeAutomaticPost extends IDraftPost {
  config: IYouTubeAutomaticPostConfig;
  postType: UniversalPostType.YouTubeAutomaticPost;
}
